import { getXdkWindow } from '@pcmp/xdk-types';
import { getCountryLanguage, getCoworkerProfile } from '@repo/utils';

export const analyticsEvents = {
    commandExecuted: 'command-executed',
    modalOpened: 'modal_opened',
    docusignSignatureRequestSent: 'docusign_signature_request_sent',
    paymentDateOptionsClicked: 'change_payment_options_clicked',
    paymentDateChanged: 'payment_due_date_changed',
} as const;

const analyticsProperties = {
    assetId: 'asset_id',
    buCode: 'userBuCode',
    coworkerId: 'userId',
    commandId: 'commandId',
    commandSource: 'command_source',
    countryCode: 'userCountry',
    hasAppendix: 'has_appendix',
    id: 'id',
    isChangePaymentDateEnabled: 'payment_change_date_enabled',
    hasPaymentDateOptions: 'has_payment_date_options',
    paymentDateChangedFrom: 'payment_date_changed_from',
    paymentDateChangedTo: 'payment_date_changed_to',
    changePaymentDateSuccess: 'change_payment_date_success',
} as const;

export const analyticsModalIds = {
    docusign: 'docusign_modal',
    questionnaire: 'questionnaire_modal',
    paymentDate: 'payment_details_modal',
    pickingList: 'picking_list_modal',
} as const;

type AnalyticsModalId = (typeof analyticsModalIds)[keyof typeof analyticsModalIds];
interface AnalyticsCustomContext {
    assetId?: string;
}
type AnalyticsContextProperty = 'asset_id' | 'userBuCode' | 'userId' | 'userCountry';

type AnalyticsContext = Partial<Record<AnalyticsContextProperty, string>>;

interface ModalOpenedAnalyticsEvent {
    eventName: typeof analyticsEvents.modalOpened;
    custom: AnalyticsCustomContext & {
        id: AnalyticsModalId;
        isChangePaymentDateEnabled?: boolean;
    };
}

interface CommandExecutedAnalyticsEvent {
    eventName: typeof analyticsEvents.commandExecuted;
    custom: AnalyticsCustomContext & {
        commandId: string;
        commandSource: string | undefined;
    };
}

interface DocusignSignatureRequestSentAnalyticsEvent {
    eventName: typeof analyticsEvents.docusignSignatureRequestSent;
    custom: AnalyticsCustomContext & {
        hasAppendix: boolean;
    };
}

interface PaymentDateOptionsClickedAnalyticsEvent {
    eventName: typeof analyticsEvents.paymentDateOptionsClicked;
    custom: AnalyticsCustomContext & {
        hasPaymentDateOptions: boolean;
    };
}

interface PaymentDateChangedAnalyticsEvent {
    eventName: typeof analyticsEvents.paymentDateChanged;
    custom: AnalyticsCustomContext & {
        paymentDateChangedFrom: string;
        paymentDateChangedTo: string;
        changePaymentDateSuccess: boolean;
    };
}

type AnalyticsEvent =
    | CommandExecutedAnalyticsEvent
    | ModalOpenedAnalyticsEvent
    | DocusignSignatureRequestSentAnalyticsEvent
    | PaymentDateOptionsClickedAnalyticsEvent
    | PaymentDateChangedAnalyticsEvent;

export const trackAnalyticsEvent = (event: AnalyticsEvent) => {
    const context = getAnalyticsContext(event.custom.assetId);
    const track = getXdkWindow()?.analytics?.track;

    switch (event.eventName) {
        case analyticsEvents.commandExecuted: {
            void track?.(event.eventName, {
                ...context,
                [analyticsProperties.commandId]: event.custom.commandId,
                [analyticsProperties.commandSource]: event.custom.commandSource,
            });
            return;
        }
        case analyticsEvents.modalOpened: {
            void track?.(event.eventName, {
                ...context,
                [analyticsProperties.id]: event.custom.id,
                ...(event.custom.isChangePaymentDateEnabled !== undefined
                    ? { [analyticsProperties.isChangePaymentDateEnabled]: event.custom.isChangePaymentDateEnabled }
                    : {}),
            });
            return;
        }
        case analyticsEvents.docusignSignatureRequestSent: {
            void track?.(event.eventName, {
                ...context,
                [analyticsProperties.hasAppendix]: event.custom.hasAppendix,
            });
            return;
        }
        case analyticsEvents.paymentDateChanged: {
            void track?.(event.eventName, {
                ...context,
                [analyticsProperties.paymentDateChangedFrom]: event.custom.paymentDateChangedFrom,
                [analyticsProperties.paymentDateChangedTo]: event.custom.paymentDateChangedTo,
                [analyticsProperties.changePaymentDateSuccess]: event.custom.changePaymentDateSuccess,
            });
            return;
        }
        case analyticsEvents.paymentDateOptionsClicked: {
            void track?.(event.eventName, {
                ...context,
                [analyticsProperties.hasPaymentDateOptions]: event.custom.hasPaymentDateOptions,
            });
            return;
        }
    }
};

const getAnalyticsContext = (assetId?: string): AnalyticsContext => {
    const { country } = getCountryLanguage();
    const coworkerProfile = getCoworkerProfile();
    const context: AnalyticsContext = {};
    const coworkerId = coworkerProfile?.data?.msalProfile?.id;
    const buCode = coworkerProfile?.data?.coworkerPreferences?.buCode;

    if (country) {
        context[analyticsProperties.countryCode] = country.toUpperCase();
    }

    if (assetId) {
        context[analyticsProperties.assetId] = assetId;
    }

    if (coworkerId) {
        context[analyticsProperties.coworkerId] = coworkerId;
    }

    if (buCode) {
        context[analyticsProperties.buCode] = buCode;
    }

    return context;
};
